import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Reveal } from '@/components/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import { PhotoComparisonSlider } from '@/components/PhotoComparisonSlider';
import {
  Sparkles,
  Sofa,
  Zap,
  CheckCircle2,
  ArrowRight,
  Wand2,
  Clock,
  Crown,
  Camera,
  Home,
  Palette,
} from 'lucide-react';
import stagingBefore from '@/assets/staging-before.jpg';
import stagingAfter from '@/assets/staging-after.jpg';

const faqs = [
  {
    q: 'What is AI virtual staging?',
    a: 'AI virtual staging adds realistic furniture, decor, and styling to photos of empty rooms — so buyers can picture themselves living there. ListBright does this in seconds, with no 3D modeling, photographers, or per-photo fees.',
  },
  {
    q: 'Is the virtual staging tool free to try?',
    a: 'Yes — try the free demo on this page (3 photos, no signup). For unlimited staging across full listings, upgrade to a paid plan starting at $15 for a single property kit.',
  },
  {
    q: 'How realistic are the staged photos?',
    a: 'Very. Our engine preserves the original room geometry, lighting, and windows, then adds tasteful furniture and decor that match the property style. You can re-roll any photo if you want a different look.',
  },
  {
    q: 'Can I stage occupied rooms or remove existing clutter?',
    a: 'Yes. Use the Declutter rule to remove personal items first, then apply Virtual Staging — or combine them in a single custom prompt.',
  },
  {
    q: 'Is virtual staging legal for real estate listings?',
    a: 'Generally yes, as long as virtually staged photos are clearly labeled. ListBright keeps the original photo so you can disclose alongside the staged version. Always check your local MLS rules.',
  },
];

const styles = [
  { name: 'Modern Minimalist', desc: 'Clean lines, neutral tones, uncluttered surfaces.' },
  { name: 'Scandinavian', desc: 'Light woods, soft textiles, plenty of whitespace.' },
  { name: 'Mid-Century Modern', desc: 'Warm woods, bold accents, iconic silhouettes.' },
  { name: 'Coastal', desc: 'Airy palette, natural fibers, relaxed layouts.' },
  { name: 'Industrial Loft', desc: 'Raw textures, leather, metal accents.' },
  { name: 'Luxury Contemporary', desc: 'Statement pieces, rich materials, gallery feel.' },
];

export default function AiVirtualStaging() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'ListBright AI Virtual Staging',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://listbright.ai/ai-virtual-staging',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      description:
        'Free AI virtual staging for real estate. Furnish empty rooms in seconds — multiple styles, photo-realistic results, no signup to try.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="AI Virtual Staging – Free Online Tool for Real Estate"
        description="Stage empty rooms with AI in seconds. Free virtual staging for real estate agents — multiple styles, photo-realistic furniture, no signup required."
        canonical="https://listbright.ai/ai-virtual-staging"
        breadcrumbs={[{ label: 'AI Virtual Staging', path: '/ai-virtual-staging' }]}
        jsonLd={jsonLd}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-border/40 py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] left-[-4rem] w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-0 px-4 py-1.5 animate-fade-in">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Free AI Tool
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] animate-fade-up">
                AI <span className="text-gradient">Virtual Staging</span> in Seconds
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up" style={{ animationDelay: '80ms' }}>
                Furnish empty rooms with photo-realistic AI staging. No 3D modeling, no
                photographers, no per-photo fees — just upload and stage.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Multiple styles</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Original preserved</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> 30 sec turnaround</span>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild size="lg" className="group shadow-lg">
                  <a href="#try">Try It Free <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/pricing">See Pricing</Link>
                </Button>
              </div>
            </div>
            <Reveal delay={120} className="relative">
              <div className="absolute -inset-5 bg-gradient-to-tr from-primary/20 via-primary/10 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
              <div className="relative rounded-2xl overflow-hidden shadow-elevated border border-border/50">
                <PhotoComparisonSlider
                  originalSrc={stagingBefore}
                  enhancedSrc={stagingAfter}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Try widget ── */}
      <section id="try" className="py-16 md:py-20 border-b border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Stage Your First Photo Free</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Upload an empty room — see it furnished in 30 seconds. 3 free demo photos, no signup.
            </p>
          </Reveal>
          <Reveal delay={80}>
            <TryItNowWidget onGetStarted={() => navigate('/auth')} />
          </Reveal>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              <Wand2 className="w-4 h-4 text-primary" /> How it works
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">How AI Virtual Staging Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Three steps. No software to install. Works on every browser.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Camera, num: '01', title: 'Upload Photo', desc: 'Drop a photo of any empty or sparsely furnished room.' },
              { icon: Wand2, num: '02', title: 'Pick a Style', desc: 'Modern, Scandinavian, Coastal, Luxury — or let our AI choose what fits the space.' },
              { icon: Sofa, num: '03', title: 'Download Staged', desc: 'Get a photo-realistic, fully furnished version in about 30 seconds.' },
            ].map(({ icon: Icon, num, title, desc }, i) => (
              <Reveal key={title} delay={i * 100}>
                <div className="card-premium relative h-full rounded-2xl border border-border/60 bg-card p-7 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-primary/50 tracking-widest">{num}</span>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Styles ── */}
      <section className="py-20 lg:py-28 border-b border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge variant="outline" className="mb-4"><Palette className="w-3.5 h-3.5 mr-1.5" /> Staging Styles</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Pick a Look That Sells</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Match the aesthetic to your buyer. Re-roll any photo if you want a different vibe.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {styles.map((s, i) => (
              <Reveal key={s.name} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-5">
                  <h3 className="font-semibold mb-1">{s.name}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why upgrade ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge className="mb-4"><Crown className="w-3.5 h-3.5 mr-1.5" /> Beyond Staging</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">
              The Complete Listing Kit
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Virtual staging is one of 10 enhancement rules included in every listing. Upgrade to
              get the full package — photos, descriptions, video, microsite — done in 10 minutes.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Sofa, title: 'Virtual Staging', desc: 'Furnish any empty room in seconds, in any style.' },
              { icon: Sparkles, title: '10 Enhancement Rules', desc: 'Declutter, golden hour, sky replace, lawn refresh — applied automatically.' },
              { icon: Home, title: '5 Description Formats', desc: 'Facebook, Instagram, Stories, Marketplace, Premium — generated in parallel.' },
              { icon: Zap, title: '28+ Languages', desc: 'Every description translated and saved in 28 languages at once.' },
              { icon: Camera, title: 'Slideshow Video', desc: 'Cinematic listing video built from your enhanced photos automatically.' },
              { icon: Clock, title: 'Done in 10 Minutes', desc: 'A complete, marketplace-ready listing — every time.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3 group">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center space-y-4">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="text-base shadow-lg">
                <Link to="/single-property-kit">
                  Get Single Property Kit – $15
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/pricing">See All Plans</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Trusted by agents in 30+ countries. Cancel anytime.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="group shadow-lg">
              <a href="#try">
                Try Virtual Staging Free
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
